import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  HelpCircle,
  BookOpen,
  PhoneForwarded,
  Mail,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { getDocsUrl, SUPPORT_EMAIL } from '@/utils/support';

interface HelpMenuProps {
  isExpanded: boolean;
  /** Mobile closes the whole sidebar drawer after a choice. */
  onNavigate?: () => void;
}

/**
 * The sidebar's Help entry.
 *
 * Help now splits in two: the documentation is a public site on its own
 * domain, and `/help` keeps only what needs the signed-in agent — chiefly the
 * forwarding tool, which fills in your own number. A popover is what lets one
 * sidebar slot lead to both without guessing which one someone wanted.
 */
export const HelpMenu: React.FC<HelpMenuProps> = ({
  isExpanded,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const goToHelp = () => {
    setOpen(false);
    onNavigate?.();
    navigate('/help');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="flex flex-row items-center justify-start">
          <Button
            variant="ghost"
            data-active={open}
            className="w-full justify-start hover:!bg-transparent hover:text-foreground dark:hover:text-foreground transition-colors duration-150 outline-none data-[active=true]:text-brand dark:data-[active=true]:text-brand data-[active=false]:text-muted-foreground dark:data-[active=false]:text-muted-foreground data-[active=false]:hover:text-foreground dark:data-[active=false]:hover:text-foreground"
          >
            <HelpCircle className="h-7 w-7" />
            <span
              className={`ml-2 transition-[opacity,transform] duration-300 ease-in-out text-lg truncate max-w-[180px] ${
                isExpanded
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0'
              }`}
            >
              {t('sidebar.help.translation')}
            </span>
          </Button>
        </div>
      </PopoverTrigger>

      <PopoverContent className="w-72 p-1.5" side="right" align="end">
        {/* Documentation opens in a new tab: it is a separate site, and
            someone reading a guide usually wants the app still open beside
            it to follow along. */}
        <a
          href={getDocsUrl()}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            setOpen(false);
            onNavigate?.();
          }}
          className="flex items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-accent"
        >
          <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
              {t('help.menu.docs.title.translation', 'Documentation')}
              <ExternalLink className="h-3 w-3 text-muted-foreground" />
            </span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              {t(
                'help.menu.docs.description.translation',
                'Guides, setup and troubleshooting'
              )}
            </span>
          </span>
        </a>

        <button
          type="button"
          onClick={goToHelp}
          className="flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-accent"
        >
          <PhoneForwarded className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-foreground">
              {t(
                'help.menu.forwarding.title.translation',
                'Call forwarding setup'
              )}
            </span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              {t(
                'help.menu.forwarding.description.translation',
                'Operator codes with your number filled in'
              )}
            </span>
          </span>
        </button>

        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          onClick={() => {
            setOpen(false);
            onNavigate?.();
          }}
          className="flex items-start gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-accent"
        >
          <Mail className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-foreground">
              {t('help.menu.support.title.translation', 'Contact support')}
            </span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              {SUPPORT_EMAIL}
            </span>
          </span>
        </a>
      </PopoverContent>
    </Popover>
  );
};

export default HelpMenu;
